import { useTranslation } from "react-i18next";
import type { AnalysisSummary, AnalysisSummaryDelta, AppAnalysisHealth } from "@repo/shared";

import { useLocale } from "@/hooks/use-locale";
import {
  deltaToneClass,
  formatBytes,
  formatSignedBytes,
  formatSignedCount,
} from "@/lib/health-format";

import { KpiCell, KpiStrip } from "./kpi";

interface CompareSummaryCardsProps {
  summaryDelta: AnalysisSummaryDelta;
  fromSummary: AnalysisSummary;
  toSummary: AnalysisSummary;
  fromHealth: AppAnalysisHealth | null;
  toHealth: AppAnalysisHealth | null;
}

/** Wasted memory as the detail screen counts it: symbol + index bytes. */
function wastedOf(summary: AnalysisSummary): number {
  return summary.wastedBytes + summary.wastedIndexBytes;
}

/** In-memory footprint, with the same fallback the detail screen uses. */
function ramOf(summary: AnalysisSummary): number {
  return summary.staticByteSize || summary.totalBytes;
}

/**
 * The compare strip: one delta per headline metric, with the two absolutes it
 * came from underneath. Deltas come from the API's `summaryDelta`, except the
 * RAM cell — its absolute has a fallback (`staticByteSize || totalBytes`), so
 * its delta is derived from the very numbers shown in the sub-line rather than
 * from a field that may not be the one being displayed.
 */
export function CompareSummaryCards({
  summaryDelta,
  fromSummary,
  toSummary,
  fromHealth,
  toHealth,
}: CompareSummaryCardsProps) {
  const { t } = useTranslation("analyses");
  const { language } = useLocale();

  const scoreDelta = summaryDelta.healthScoreDelta;
  const showScore = scoreDelta !== undefined && fromHealth != null && toHealth != null;

  const wastedDelta = summaryDelta.wastedBytesDelta + summaryDelta.wastedIndexBytesDelta;
  const ramDelta = ramOf(toSummary) - ramOf(fromSummary);

  const range = (from: string | number, to: string | number) =>
    t("compare.kpi.range", { from, to });
  const count = (value: number) => value.toLocaleString(language);

  return (
    <KpiStrip>
      {showScore && fromHealth && toHealth && (
        <KpiCell
          label={t("summary.score")}
          value={formatSignedCount(scoreDelta, language)}
          valueClassName={deltaToneClass(scoreDelta, "higher-is-better")}
          subMeta={t("compare.kpi.scoreRange", {
            from: fromHealth.score,
            to: toHealth.score,
            grade: t(`grade.${toHealth.grade}`),
          })}
        />
      )}
      <KpiCell
        label={t("summary.wastedMemory")}
        value={formatSignedBytes(wastedDelta)}
        valueClassName={deltaToneClass(wastedDelta, "lower-is-better")}
        subMeta={range(formatBytes(wastedOf(fromSummary)), formatBytes(wastedOf(toSummary)))}
      />
      <KpiCell
        label={t("summary.appRam")}
        value={formatSignedBytes(ramDelta)}
        valueClassName={deltaToneClass(ramDelta, "lower-is-better")}
        subMeta={range(formatBytes(ramOf(fromSummary)), formatBytes(ramOf(toSummary)))}
      />
      <KpiCell
        label={t("summary.totalFields")}
        value={formatSignedCount(summaryDelta.totalFieldsDelta, language)}
        valueClassName={deltaToneClass(summaryDelta.totalFieldsDelta, "neutral")}
        subMeta={range(count(fromSummary.totalFields), count(toSummary.totalFields))}
      />
      <KpiCell
        label={t("compare.kpi.unusedFields")}
        value={formatSignedCount(summaryDelta.unusedFieldsDelta, language)}
        valueClassName={deltaToneClass(summaryDelta.unusedFieldsDelta, "lower-is-better")}
        subMeta={range(count(fromSummary.unusedFields), count(toSummary.unusedFields))}
      />
      <KpiCell
        label={t("compare.kpi.brokenMeasures")}
        value={formatSignedCount(summaryDelta.brokenMeasuresDelta, language)}
        valueClassName={deltaToneClass(summaryDelta.brokenMeasuresDelta, "lower-is-better")}
        subMeta={range(count(fromSummary.brokenMeasures), count(toSummary.brokenMeasures))}
      />
    </KpiStrip>
  );
}
